/**
 * Turn UX-A A.3 — Modal signature manuelle depuis le pipeline (drag → colonne "Signé").
 *
 * Flow :
 * 1. Charge les devis signables (status=envoye) du lead
 * 2. Le pro sélectionne le devis + coche/décoche "Générer facture d'acompte"
 * 3. Submit → POST /api/leads/{id}/mark-signed
 * 4. Toast succès avec lien PDF facture si générée → onSuccess()
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Check, AlertCircle, FileText } from "lucide-react";
import { api, formatApiError, API_ROOT } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

function formatEur(v) {
  return new Intl.NumberFormat("fr-BE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(Number(v) || 0);
}

export default function MarkSignedModal({ open, onOpenChange, card, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [selectedQuoteId, setSelectedQuoteId] = useState("");
  const [generateDeposit, setGenerateDeposit] = useState(true);

  useEffect(() => {
    if (!open || !card?.id) return;
    setLoading(true);
    setQuotes([]);
    setSelectedQuoteId("");
    setGenerateDeposit(true);
    (async () => {
      try {
        const { data } = await api.get(`/leads/${card.id}/signable-quotes`);
        setQuotes(data.quotes || []);
        if (data.quotes?.length === 1) {
          setSelectedQuoteId(data.quotes[0].id);
        }
      } catch (e) {
        toast.error("Chargement échoué", { description: formatApiError(e) });
      } finally {
        setLoading(false);
      }
    })();
  }, [open, card?.id]);

  const selectedQuote = quotes.find((q) => q.id === selectedQuoteId);

  const handleSubmit = async () => {
    if (!selectedQuoteId || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/leads/${card.id}/mark-signed`, {
        quote_id: selectedQuoteId,
        generate_deposit: generateDeposit,
      });
      const invoice = data.invoice;
      toast.success("Projet marqué comme signé", {
        description: invoice
          ? `Acompte ${invoice.number} · ${formatEur(invoice.total_tvac)}`
          : "Aucune facture d'acompte générée.",
        action: invoice
          ? {
              label: "PDF",
              onClick: () =>
                window.open(
                  `${API_ROOT.replace(/\/api$/, "")}${invoice.pdf_download_url}`,
                  "_blank",
                ),
            }
          : undefined,
      });
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (e) {
      toast.error("Signature échouée", { description: formatApiError(e) });
    } finally {
      setSubmitting(false);
    }
  };

  const chantierTitle = card?.title || card?.client_name || "ce projet";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg border-slate-700 bg-slate-900 text-slate-100"
        data-testid="mark-signed-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            Marquer le projet <span className="text-[#C4553B]">{chantierTitle}</span> comme signé
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-400">
            Sélectionnez le devis signé et choisissez si vous voulez générer la facture
            d'acompte immédiatement.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            Chargement des devis…
          </div>
        ) : quotes.length === 0 ? (
          <div className="rounded border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-100">
            <p className="flex items-center gap-2 font-semibold">
              <AlertCircle className="h-4 w-4" />
              Aucun devis envoyé sur ce projet
            </p>
            <p className="mt-1 text-xs text-amber-200/80">
              Créez d'abord un devis, puis envoyez-le au client (ou marquez-le "envoyé")
              avant de pouvoir le signer manuellement.
            </p>
          </div>
        ) : (
          <>
            <div>
              <Label className="text-xs text-slate-400">Devis à signer</Label>
              <div className="mt-2 space-y-2">
                {quotes.map((q) => (
                  <button
                    key={q.id}
                    onClick={() => setSelectedQuoteId(q.id)}
                    className={`flex w-full items-center justify-between rounded border px-3 py-2 text-left text-sm transition-colors ${
                      selectedQuoteId === q.id
                        ? "border-[#C4553B] bg-[#C4553B]/10 text-slate-100"
                        : "border-slate-700 bg-slate-800/40 text-slate-300 hover:bg-slate-700/40"
                    }`}
                    data-testid={`marksigned-quote-${q.id}`}
                  >
                    <span className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-slate-400" />
                      <span>
                        <span className="font-medium">{q.number || "Sans n°"}</span>
                        {q.title && (
                          <span className="ml-2 text-xs text-slate-500">— {q.title}</span>
                        )}
                      </span>
                    </span>
                    <span className="font-mono text-sm text-slate-100">
                      {formatEur(q.total_tvac)}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {selectedQuote && (
              <div className="mt-3 rounded border border-slate-700 bg-slate-800/40 p-3">
                <label className="flex cursor-pointer items-start gap-2">
                  <Checkbox
                    checked={generateDeposit}
                    onCheckedChange={(v) => setGenerateDeposit(!!v)}
                    className="mt-0.5 border-slate-500 data-[state=checked]:bg-[#C4553B] data-[state=checked]:border-[#C4553B]"
                    data-testid="marksigned-generate-deposit"
                  />
                  <span className="text-sm">
                    <span className="font-medium text-slate-100">
                      Générer la facture d'acompte
                    </span>
                    <p className="mt-0.5 text-xs text-slate-400">
                      Basée sur l'échéancier configuré pour ce chantier (généralement
                      30–40 % du TVAC). Le pourcentage exact provient de la config
                      company. Décochez si vous préférez la générer plus tard.
                    </p>
                  </span>
                </label>
              </div>
            )}
          </>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-slate-300 hover:bg-slate-800 hover:text-slate-100"
            data-testid="marksigned-cancel-btn"
          >
            Annuler
          </Button>
          <Button
            disabled={!selectedQuoteId || submitting || quotes.length === 0}
            onClick={handleSubmit}
            className="bg-[#C4553B] text-white hover:bg-[#A8402C] disabled:opacity-50"
            data-testid="marksigned-submit-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Signature…
              </>
            ) : (
              <>
                <Check className="mr-2 h-4 w-4" />
                Confirmer la signature
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
